import React, { createContext, useState, useEffect, useContext } from 'react';
import { useFirebase } from '../context/firebaseContext';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

export const AcademyContext = createContext();

export const AcademyProvider = ({ children }) => {
  const { auth, db } = useFirebase();
  const [user, setUser] = useState(null);
  const [progress, setProgress] = useState({
    completedChapters: 0,
    completedLessons: 0,
    // Course-specific progress
    astrology: { 
      completedLessons: 0,
      questionProgress: {} // Store question completion for each lesson
    },
    tarot: { 
      completedLessons: 0,
      questionProgress: {}
    },
    runes: { 
      completedLessons: 0,
      questionProgress: {}
    },
    numerology: { 
      completedLessons: 0,
      questionProgress: {}
    },
    kabbalah: { 
      completedLessons: 0,
      questionProgress: {}
    },
  });
  const [selectedMode, setSelectedMode] = useState("beginner");
  const [currentLesson, setCurrentLesson] = useState(progress.completedLessons + 1);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, [auth]);

  useEffect(() => {
    const fetchProgress = async () => {
      if (user) {
        const progressRef = doc(db, 'academyprogress', user.uid);
        const docSnap = await getDoc(progressRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          // Ensure course-specific progress exists with question progress
          const updatedProgress = {
            completedChapters: data.completedChapters || 0,
            completedLessons: data.completedLessons || 0,
            astrology: {
              completedLessons: data.astrology?.completedLessons || 0,
              questionProgress: data.astrology?.questionProgress || {}
            },
            tarot: {
              completedLessons: data.tarot?.completedLessons || 0,
              questionProgress: data.tarot?.questionProgress || {}
            },
            runes: {
              completedLessons: data.runes?.completedLessons || 0,
              questionProgress: data.runes?.questionProgress || {}
            },
            numerology: {
              completedLessons: data.numerology?.completedLessons || 0,
              questionProgress: data.numerology?.questionProgress || {}
            },
            kabbalah: {
              completedLessons: data.kabbalah?.completedLessons || 0,
              questionProgress: data.kabbalah?.questionProgress || {}
            },
          };
          setProgress(updatedProgress);
        } else {
          const initialProgress = {
            completedChapters: 0,
            completedLessons: 0,
            astrology: { completedLessons: 0, questionProgress: {} },
            tarot: { completedLessons: 0, questionProgress: {} },
            runes: { completedLessons: 0, questionProgress: {} },
            numerology: { completedLessons: 0, questionProgress: {} },
            kabbalah: { completedLessons: 0, questionProgress: {} },
          };
          await setDoc(progressRef, initialProgress);
          setProgress(initialProgress);
        }
      }
    };

    fetchProgress();
  }, [user, db]);

  const updateProgress = async (newProgress) => {
    if (user) {
      const progressRef = doc(db, 'academyprogress', user.uid);
      
      // Ensure we have valid progress data
      const safeProgress = {
        completedChapters: newProgress.completedChapters || 0,
        completedLessons: newProgress.completedLessons || 0,
        astrology: {
          completedLessons: newProgress.astrology?.completedLessons || 0,
          questionProgress: newProgress.astrology?.questionProgress || {}
        },
        tarot: {
          completedLessons: newProgress.tarot?.completedLessons || 0,
          questionProgress: newProgress.tarot?.questionProgress || {}
        },
        runes: {
          completedLessons: newProgress.runes?.completedLessons || 0,
          questionProgress: newProgress.runes?.questionProgress || {}
        },
        numerology: {
          completedLessons: newProgress.numerology?.completedLessons || 0,
          questionProgress: newProgress.numerology?.questionProgress || {}
        },
        kabbalah: {
          completedLessons: newProgress.kabbalah?.completedLessons || 0,
          questionProgress: newProgress.kabbalah?.questionProgress || {}
        },
      };
      
      await setDoc(progressRef, safeProgress, { merge: true });
      setProgress(safeProgress);
    }
  };

  const updateCourseProgress = async (course, completedLessons) => {
    if (user) {
      const progressRef = doc(db, 'academyprogress', user.uid);
      
      // Ensure we have valid progress data
      const safeProgress = {
        completedChapters: progress.completedChapters || 0,
        completedLessons: progress.completedLessons || 0,
        astrology: {
          completedLessons: progress.astrology?.completedLessons || 0,
          questionProgress: progress.astrology?.questionProgress || {}
        },
        tarot: {
          completedLessons: progress.tarot?.completedLessons || 0,
          questionProgress: progress.tarot?.questionProgress || {}
        },
        runes: {
          completedLessons: progress.runes?.completedLessons || 0,
          questionProgress: progress.runes?.questionProgress || {}
        },
        numerology: {
          completedLessons: progress.numerology?.completedLessons || 0,
          questionProgress: progress.numerology?.questionProgress || {}
        },
        kabbalah: {
          completedLessons: progress.kabbalah?.completedLessons || 0,
          questionProgress: progress.kabbalah?.questionProgress || {}
        },
      };
      
      const newProgress = {
        ...safeProgress,
        [course]: { 
          ...safeProgress[course],
          completedLessons 
        }
      };
      await setDoc(progressRef, newProgress, { merge: true });
      setProgress(newProgress);
    }
  };

  const updateQuestionProgress = async (course, lessonNumber, questionIndex, isCompleted) => {
    if (user) {
      const progressRef = doc(db, 'academyprogress', user.uid);
      
      // Ensure we have valid progress data
      const safeProgress = {
        completedChapters: progress.completedChapters || 0,
        completedLessons: progress.completedLessons || 0,
        astrology: {
          completedLessons: progress.astrology?.completedLessons || 0,
          questionProgress: progress.astrology?.questionProgress || {}
        },
        tarot: {
          completedLessons: progress.tarot?.completedLessons || 0,
          questionProgress: progress.tarot?.questionProgress || {}
        },
        runes: {
          completedLessons: progress.runes?.completedLessons || 0,
          questionProgress: progress.runes?.questionProgress || {}
        },
        numerology: {
          completedLessons: progress.numerology?.completedLessons || 0,
          questionProgress: progress.numerology?.questionProgress || {}
        },
        kabbalah: {
          completedLessons: progress.kabbalah?.completedLessons || 0,
          questionProgress: progress.kabbalah?.questionProgress || {}
        },
      };
      
      const currentQuestionProgress = safeProgress[course]?.questionProgress || {};
      const lessonQuestionProgress = currentQuestionProgress[lessonNumber] || [];
      
      // Update the specific question completion status
      const updatedLessonProgress = [...lessonQuestionProgress];
      updatedLessonProgress[questionIndex] = isCompleted;
      
      const newQuestionProgress = {
        ...currentQuestionProgress,
        [lessonNumber]: updatedLessonProgress
      };
      
      const newProgress = {
        ...safeProgress,
        [course]: {
          ...safeProgress[course],
          questionProgress: newQuestionProgress
        }
      };
      
      await setDoc(progressRef, newProgress, { merge: true });
      setProgress(newProgress);
    }
  };

  const getQuestionProgress = (course, lessonNumber) => {
    return progress[course]?.questionProgress?.[lessonNumber] || [];
  };

  const updateMode = (mode) => {
    setSelectedMode(mode);
  };

  const updateCurrentLesson = (lesson) => {
    setCurrentLesson(lesson);
  };

  return (
    <AcademyContext.Provider value={{ 
      progress, 
      updateProgress, 
      updateCourseProgress,
      updateQuestionProgress,
      getQuestionProgress,
      selectedMode, 
      updateMode, 
      currentLesson, 
      updateCurrentLesson 
    }}>
      {children}
    </AcademyContext.Provider>
  );
};

export const useAcademy = () => {
  const context = useContext(AcademyContext);
  if (!context) {
    throw new Error('useAcademy must be used within an AcademyProvider');
  }
  return context;
};
